import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { teamAPI } from '../services/api';
import { useSiteSettings } from '../hooks/useContent';
import { usePageSeo } from '../hooks/usePageSeo';
import { buildOrganizationSchema, buildBreadcrumbSchema } from '../utils/seo';
import Founders from '../components/Team/Founders';
import TeamSocialLinks from '../components/Team/TeamSocialLinks';
import LoadingIndicator from '../components/LoadingIndicator';
import RichContent from '../components/RichContent';
import { pushDocumentScrollLock, popDocumentScrollLock } from '../utils/documentScrollLock';
import { optimizeImageUrl, IMAGE_DISPLAY_WIDTH } from '../utils/optimizeImageUrl.js';
import './Blogs.css';
import './Team.css';


const SECTION_KEYS = ['head_of_projects', 'core_team', 'office_team', 'field_team', 'volunteers', 'alumni'];

function memberHasBio(member) {
  const raw = String(member?.bio ?? '').trim();
  if (!raw) return false;
  const text = raw
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > 0;
}

const defaultData = {
  hero: {
    title: 'Meet Our Team',
    subtitle: 'Driven by passion, united by purpose. Meet the people behind the adventures.',
    heroImageUrl: ''
  },
  featured: [],
  sections: SECTION_KEYS.reduce((acc, k) => {
    acc[k] = { key: k, label: k.replace(/_/g, ' '), intro: '', members: [] };
    return acc;
  }, {})
};

const Team = () => {
  const [data, setData] = useState(defaultData);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('head_of_projects');
  const [bioModalMember, setBioModalMember] = useState(null);
  const { settings } = useSiteSettings();

  const publicBase =
    String(settings.site_public_url || '').replace(/\/$/, '') ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  const brand = settings.site_brand_name || 'Invincible NGO';

  const teamSchema = useMemo(() => {
    const org = buildOrganizationSchema(settings);
    const breadcrumbs = buildBreadcrumbSchema([
      { name: 'Meet Our Team', url: '/team' }
    ], publicBase);
    return {
      '@context': 'https://schema.org',
      '@graph': [org, breadcrumbs]
    };
  }, [settings, publicBase]);

  usePageSeo({
    title: `${data.hero?.title || 'Meet Our Team'} | ${brand}`,
    description: data.hero?.subtitle || 'Driven by passion, united by purpose. Meet the certified leaders and team behind Invincible NGO.',
    canonical: publicBase ? `${publicBase}/team` : undefined,
    image: data.hero?.heroImageUrl || undefined,
    schema: teamSchema
  });


  useEffect(() => {
    let cancelled = false;
    teamAPI.getTeam()
      .then((res) => {
        const payload = res.data?.data || res.data;
        if (payload && !cancelled) setData({ ...defaultData, ...payload });
      })
      .catch(() => { if (!cancelled) setData(defaultData); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!bioModalMember) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setBioModalMember(null);
    };
    document.addEventListener('keydown', onKey);
    pushDocumentScrollLock();
    return () => {
      document.removeEventListener('keydown', onKey);
      popDocumentScrollLock();
    };
  }, [bioModalMember]);

  const { hero, sections, featured } = data;
  const currentSection = sections[activeSection] || sections.core_team || sections.head_of_projects;
  const featuredIds = new Set((featured || []).map((m) => String(m?.id || '')));
  const members = (currentSection?.members || []).filter((member) => {
    const id = String(member?.id || '');
    return id ? !featuredIds.has(id) : true;
  });
  const heroImage = optimizeImageUrl(String(hero?.heroImageUrl || '').trim(), {
    width: IMAGE_DISPLAY_WIDTH.hero
  });
  const heroClassName = heroImage
    ? 'blog-hero-banner blog-hero-banner-with-image'
    : 'blog-hero-banner';

  return (
    <div className="team-page bg-background-light dark:bg-background-dark">
      <section
        className={heroClassName}
        style={
          heroImage
            ? {
                backgroundImage: `linear-gradient(135deg, rgba(15,23,42,0.9) 0%, rgba(15,23,42,0.75) 40%, rgba(15,23,42,0.55) 100%), url(${heroImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }
            : undefined
        }
      >
        <div className="blog-hero-inner">
          <h1 className="blog-hero-title">{hero.title || 'Meet Our Team'}</h1>
          <p className="blog-hero-subtitle">
            {hero.subtitle ||
              'Driven by passion, united by purpose. Meet the people behind the adventures.'}
          </p>
        </div>
      </section>

      {/* Founders Section */}
      <Founders founders={featured} />

      {/* Sticky category navigation */}
      <div className="team-category-bar">
        <div className="team-category-scroll">
          {SECTION_KEYS.map((key) => {
            const label = (sections[key]?.label || key).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            return (
              <button
                key={key}
                type="button"
                className={`team-category-tab ${activeSection === key ? 'active' : ''}`}
                onClick={() => setActiveSection(key)}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Team grid — no section title/intro; tabs + member cards only */}
      <section className="team-grid-section max-w-7xl mx-auto w-full">
        {loading ? (
          <div className="team-loading text-center py-10">
            <LoadingIndicator label="Loading team..." />
          </div>
        ) : (
          <div className="team-members-grid">
            {members.map((member) => {
              const hasBio = memberHasBio(member);
              return (
                <div key={member.id} className="team-member-card-vertical">
                  <div
                    className={`team-member-media${hasBio ? ' team-member-media--clickable' : ''}`}
                    role={hasBio ? 'button' : undefined}
                    tabIndex={hasBio ? 0 : undefined}
                    aria-label={hasBio ? `View ${member.fullName || 'team member'} bio` : undefined}
                    onClick={hasBio ? () => setBioModalMember(member) : undefined}
                    onKeyDown={
                      hasBio
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setBioModalMember(member);
                            }
                          }
                        : undefined
                    }
                  >
                    {member.profilePicUrl ? (
                      <img
                        src={optimizeImageUrl(member.profilePicUrl, { width: IMAGE_DISPLAY_WIDTH.card })}
                        alt={member.fullName}
                        className="team-member-media-image"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className="team-member-media-placeholder" />
                    )}
                  </div>

                  <div className="team-member-body">
                    <h3 className="team-card-name">{member.fullName}</h3>
                    {member.designation && (
                      <p className="team-card-role">{String(member.designation).trim()}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && members.length === 0 && (
          <p className="team-no-members text-center pb-20">No team members in this section yet.</p>
        )}
      </section>

      {bioModalMember && (
        <div
          className="team-bio-modal-backdrop"
          role="presentation"
          onClick={() => setBioModalMember(null)}
        >
          <div
            className="team-bio-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="team-bio-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="team-bio-modal-close modal-close-circle"
              aria-label="Close"
              onClick={() => setBioModalMember(null)}
            >
              ×
            </button>
            <h2 id="team-bio-modal-title" className="team-bio-modal-name">
              {bioModalMember.fullName}
            </h2>
            {bioModalMember.designation ? (
              <p className="team-bio-modal-role">{String(bioModalMember.designation).trim()}</p>
            ) : null}
            <div className="team-bio-modal-socials">
              <TeamSocialLinks member={bioModalMember} />
            </div>
            <div className="team-bio-modal-body">
              <RichContent
                content={bioModalMember.bio}
                className="team-bio-modal-rich"
                stripQuillBlankParagraphs
              />
            </div>
          </div>
        </div>
      )}

      {/* CTA strip */}
      <section className="team-cta-strip">
        <div className="team-cta-inner">
          <div className="team-cta-text">
            <h3>Join Invincible as Volunteer</h3>
            <p>Be part of the community that powers every expedition.</p>
          </div>
          <div className="team-cta-actions">
            <Link to="/about" className="team-cta-btn-outline">
              Learn More
            </Link>
            <Link to="/careers" className="team-cta-btn-primary">
              Join The Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Team;
